import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, X } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "../lib/api";
import { optImg } from "../lib/assets";
import { useShop } from "../context/ShopContext";
import { Reveal } from "./Reveal";

const GENDER_TABS = [["all", "All"], ["men", "Men"], ["women", "Women"]];

function QuickView({ product, onClose }) {
  const { addToCart, setCartOpen } = useShop();
  const reduce = useReducedMotion();
  const [img, setImg] = useState(0);
  const [color, setColor] = useState(product.colors?.[0]?.name || "");
  const sizeStock = product.size_stock || {};
  const isSoldOut = useCallback((s) => (s in sizeStock ? (sizeStock[s] ?? 0) <= 0 : (product.stock ?? 0) <= 0), [sizeStock, product.stock]);
  const firstAvailable = (product.sizes || []).find((s) => !isSoldOut(s));
  const [size, setSize] = useState(firstAvailable || product.sizes?.[0] || "One Size");
  const allSoldOut = (product.sizes || []).length > 0 && (product.sizes || []).every((s) => isSoldOut(s));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const images = product.images?.length ? product.images : [""];

  const add = () => {
    if (isSoldOut(size)) {
      toast.error("This size is sold out");
      return;
    }
    addToCart(product, size, color);
    setCartOpen(true);
    onClose();
  };

  return (
    <motion.div data-testid="quickview-overlay" className="fixed inset-0 z-[90] flex items-end md:items-center justify-center"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduce ? 0 : 0.3 }}>
      <div className="absolute inset-0 bg-vv-black/55 backdrop-blur-[2px]" onClick={onClose} />
      <motion.div
        initial={reduce ? false : { y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={reduce ? { opacity: 0 } : { y: 40, opacity: 0 }}
        transition={{ duration: reduce ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative bg-white w-full md:max-w-4xl max-h-[92vh] md:max-h-[86vh] overflow-y-auto md:grid md:grid-cols-2"
        role="dialog" aria-modal="true" aria-label={product.name}>
        <button data-testid="quickview-close" onClick={onClose} autoFocus aria-label="Close product details"
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-vv-black/10 hover:bg-vv-black hover:text-vv-ivory transition-colors">
          <X size={18} strokeWidth={1.5} />
        </button>

        <div className="bg-vv-elevated">
          <div className="aspect-[3/4] md:aspect-auto md:h-full relative overflow-hidden">
            <AnimatePresence mode="wait" initial={false}>
              <motion.img key={images[img]} src={optImg(images[img], 1000)} alt={product.name} decoding="async"
                initial={reduce ? false : { opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: reduce ? 0 : 0.45, ease: "easeOut" }}
                className="w-full h-full object-cover" />
            </AnimatePresence>
          </div>
          {images.length > 1 && (
            <div className="absolute bottom-4 left-4 flex gap-2">
              {images.slice(0, 4).map((src, i) => (
                <button key={src + i} data-testid={`quickview-thumb-${i}`} onClick={() => setImg(i)}
                  aria-label={i === 0 ? "Front view" : i === 1 ? "Back view" : `View ${i + 1}`}
                  className={`w-14 aspect-[3/4] overflow-hidden border-2 transition-colors ${i === img ? "border-vv-black" : "border-white/60 opacity-70 hover:opacity-100"}`}>
                  <img src={optImg(src, 200)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 md:p-10 flex flex-col">
          <p className="vv-overline text-vv-muted mb-3 capitalize">{product.gender || "Unisex"} · {(product.category || "").replace(/-/g, " ")}</p>
          <h3 data-testid="quickview-name" className="font-display text-2xl md:text-3xl tracking-tight leading-tight">{product.name}</h3>
          <p data-testid="quickview-price" className="mt-3 text-lg">
            {product.compare_at > product.price && <span className="text-sm text-vv-stone line-through mr-2">{inr(product.compare_at)}</span>}
            {inr(product.price)}
          </p>

          {(product.colors || []).length > 0 && (
            <div className="mt-7">
              <p className="text-[10px] uppercase tracking-[0.2em] text-vv-muted mb-3">Colour — {color}</p>
              <div className="flex gap-2.5">
                {product.colors.map((c) => (
                  <button key={c.name} data-testid={`quickview-color-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setColor(c.name)} title={c.name} aria-label={`Colour ${c.name}`} aria-pressed={color === c.name}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${color === c.name ? "border-vv-black scale-110" : "border-vv-black/15 hover:border-vv-black/40"}`}
                    style={{ background: c.hex }} />
                ))}
              </div>
            </div>
          )}

          {(product.sizes || []).length > 0 && (
            <div className="mt-7">
              <p className="text-[10px] uppercase tracking-[0.2em] text-vv-muted mb-3">Size{isSoldOut(size) ? " — sold out" : ""}</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button key={s} data-testid={`quickview-size-${s.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => !isSoldOut(s) && setSize(s)} disabled={isSoldOut(s)} aria-pressed={size === s}
                    className={`min-w-11 px-3 py-2.5 text-[11px] tracking-widest border transition-colors ${isSoldOut(s) ? "border-vv-black/10 text-vv-stone line-through cursor-not-allowed" : size === s ? "bg-vv-black text-vv-ivory border-vv-black" : "border-vv-black/20 hover:border-vv-black"}`}>
                    {s}
                  </button>
                ))}
              </div>
              {allSoldOut && <p data-testid="quickview-sold-out" className="text-[11px] text-vv-burgundy mt-3">This piece is currently sold out.</p>}
            </div>
          )}

          {product.description && <p className="text-sm text-vv-muted leading-relaxed font-light mt-7 line-clamp-4">{product.description}</p>}

          <div className="mt-auto pt-8 space-y-3">
            <button data-testid="quickview-add-to-bag" onClick={add} disabled={allSoldOut}
              className="vv-btn-dark w-full disabled:opacity-40 disabled:cursor-not-allowed">
              {allSoldOut ? "Sold Out" : "Add to Bag"}
            </button>
            <Link data-testid="quickview-view-details" to={`/product/${product.slug}`} onClick={onClose}
              className="vv-btn-outline-dark w-full inline-flex items-center justify-center gap-2">
              View in Shop <ArrowUpRight size={13} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function NewArrivals() {
  const reduce = useReducedMotion();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("all");
  const [index, setIndex] = useState(0);
  const [quickView, setQuickView] = useState(null);
  const trackRef = useRef(null);
  const [trackW, setTrackW] = useState(0);

  useEffect(() => {
    api.get("/products/new-arrivals").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    const measure = () => trackRef.current && setTrackW(trackRef.current.offsetWidth);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [items]);

  const filtered = (items || []).filter((p) => tab === "all" || p.gender === tab || p.gender === "unisex");
  const active = Math.min(index, Math.max(0, filtered.length - 1));
  const current = filtered[active];

  const isMobile = trackW < 768;
  const slideW = isMobile ? Math.round(trackW * 0.74) : Math.min(380, Math.round(trackW * 0.3));
  const gap = isMobile ? 14 : 28;
  const maxX = 0;
  const minX = -(Math.max(0, filtered.length * (slideW + gap) - gap - trackW));
  const centerOffset = Math.max(0, (trackW - slideW) / 2);
  const x = Math.max(minX, Math.min(maxX, -(active * (slideW + gap)) + centerOffset));

  const go = useCallback((i) => setIndex((v) => Math.max(0, Math.min(filtered.length - 1, typeof i === "function" ? i(v) : i))), [filtered.length]);

  const onDragEnd = (_, info) => {
    if (info.offset.x < -50 || info.velocity.x < -400) go(active + 1);
    else if (info.offset.x > 50 || info.velocity.x > 400) go(active - 1);
  };

  const onKey = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); go(active - 1); }
    if (e.key === "ArrowRight") { e.preventDefault(); go(active + 1); }
  };

  if (items === null) {
    return <section data-testid="new-arrivals-section" className="py-24 md:py-36 px-4"><div className="h-[60vh] max-h-[560px] bg-vv-elevated/50 animate-pulse max-w-[1600px] mx-auto" /></section>;
  }

  return (
    <section data-testid="new-arrivals-section" className="py-20 md:py-32 overflow-hidden" aria-roledescription="carousel" aria-label="New arrivals">
      <div className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="vv-overline text-vv-muted mb-4">Just Landed</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">NEW ARRIVALS</h2>
            <p className="text-vv-muted text-sm md:text-base font-light mt-4 max-w-md">Discover the latest expressions of contemporary style.</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex gap-5" data-testid="new-arrivals-tabs">
              {GENDER_TABS.map(([val, label]) => (
                <button key={val} data-testid={`new-arrivals-tab-${val}`}
                  onClick={() => { setTab(val); setIndex(0); }}
                  className={`text-[11px] uppercase tracking-[0.25em] pb-1 transition-colors ${tab === val ? "border-b border-vv-black font-medium" : "text-vv-muted hover:text-vv-black"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="hidden sm:flex gap-2">
              <button data-testid="new-arrivals-prev" onClick={() => go(active - 1)} disabled={active === 0} aria-label="Previous product"
                className="w-11 h-11 rounded-full border border-vv-black/20 flex items-center justify-center hover:bg-vv-black hover:text-vv-ivory transition-colors disabled:opacity-25 disabled:pointer-events-none">
                <ArrowLeft size={15} strokeWidth={1.5} />
              </button>
              <button data-testid="new-arrivals-next" onClick={() => go(active + 1)} disabled={active >= filtered.length - 1} aria-label="Next product"
                className="w-11 h-11 rounded-full border border-vv-black/20 flex items-center justify-center hover:bg-vv-black hover:text-vv-ivory transition-colors disabled:opacity-25 disabled:pointer-events-none">
                <ArrowRight size={15} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="new-arrivals-empty" className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto text-center py-16 border border-vv-black/10">
          <p className="font-display text-2xl tracking-tight">New pieces are on their way.</p>
          <p className="text-sm text-vv-muted mt-3 font-light">The next drop is being prepared in the studio.</p>
          <Link to="/shop" className="vv-btn-outline-dark mt-8 inline-block">Browse the Collection</Link>
        </div>
      ) : (
        <Reveal y={30}>
          <div ref={trackRef} tabIndex={0} onKeyDown={onKey} aria-label="Browse new arrivals with arrow keys"
            className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto outline-none focus-visible:ring-1 focus-visible:ring-vv-black/40">
            <motion.div className="flex cursor-grab active:cursor-grabbing" style={{ gap, touchAction: "pan-y" }}
              drag={reduce ? false : "x"} dragConstraints={{ left: minX, right: maxX + centerOffset * 0 }} dragElastic={0.08}
              onDragEnd={onDragEnd}
              animate={{ x }} transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 220, damping: 32 }}>
              {filtered.map((p, i) => {
                const isActive = i === active;
                return (
                  <div key={p.id} data-testid={`new-arrivals-slide-${p.slug}`} aria-hidden={!isActive}
                    className="shrink-0 select-none" style={{ width: slideW }}>
                    <motion.button type="button" onClick={() => (isActive ? setQuickView(p) : go(i))}
                      animate={reduce ? {} : { scale: isActive ? 1 : 0.94, opacity: isActive ? 1 : 0.5 }}
                      transition={{ duration: reduce ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="block w-full text-left group"
                      aria-label={isActive ? `View ${p.name}` : `Go to ${p.name}`} tabIndex={-1}>
                      <div className="relative overflow-hidden bg-vv-elevated aspect-[3/4]">
                        <img src={optImg(p.images?.[0], 800)} alt={p.name} loading={Math.abs(i - active) <= 1 ? "eager" : "lazy"}
                          decoding="async" draggable={false}
                          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                        {p.images?.[1] && isActive && (
                          <img src={optImg(p.images[1], 800)} alt="" aria-hidden loading="lazy" decoding="async" draggable={false}
                            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                        )}
                        <span className="absolute top-3 left-3 text-[9px] tracking-[0.25em] px-2.5 py-1 bg-vv-forest text-vv-ivory">NEW</span>
                      </div>
                    </motion.button>
                  </div>
                );
              })}
            </motion.div>
          </div>

          <div className="px-4 sm:px-8 lg:px-16 max-w-[1600px] mx-auto mt-10 md:mt-14">
            <AnimatePresence mode="wait" initial={false}>
              {current && (
                <motion.div key={current.id} data-testid="new-arrivals-info"
                  initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: reduce ? 0 : 0.35, ease: "easeOut" }}
                  className="flex flex-wrap items-center justify-between gap-6 border-t border-vv-black/10 pt-8">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-vv-stone mb-2">
                      <span data-testid="new-arrivals-position">{String(active + 1).padStart(2, "0")} / {String(filtered.length).padStart(2, "0")}</span>
                      {(current.colors || []).length > 0 && <span data-testid="new-arrivals-colours" className="ml-4">{current.colors.map((c) => c.name).join(" · ")}</span>}
                    </p>
                    <p className="font-display text-xl md:text-2xl tracking-tight">{current.name}</p>
                    <p className="text-sm text-vv-muted mt-1.5">
                      {current.compare_at > current.price && <span className="line-through mr-2 text-vv-stone">{inr(current.compare_at)}</span>}
                      {inr(current.price)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      {filtered.map((_, i) => (
                        <button key={i} data-testid={`new-arrivals-dot-${i}`} onClick={() => go(i)} aria-label={`Go to product ${i + 1}`}
                          className={`h-1 transition-all duration-400 ${i === active ? "w-8 bg-vv-black" : "w-3 bg-vv-black/20 hover:bg-vv-black/40"}`} />
                      ))}
                    </div>
                    <button data-testid="new-arrivals-explore" onClick={() => setQuickView(current)}
                      className="vv-btn-dark ml-3">
                      Explore Product <ArrowUpRight size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      )}

      <AnimatePresence>
        {quickView && <QuickView product={quickView} onClose={() => setQuickView(null)} />}
      </AnimatePresence>
    </section>
  );
}
